import { useEffect, useRef, type FC } from 'react';
import { LuExternalLink, LuMic, LuImage, LuVideo, LuX } from 'react-icons/lu';
import type { ConversaSemTexto } from '../services/AssistenteService';
import './AssistenteSemTexto.css';

const data = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—';

/**
 * As conversas que a varredura do funil não conseguiu julgar (25/10/2026).
 *
 * **De onde veio.** Toda resposta da varredura termina com "N conversas não
 * tinham texto suficiente para avaliar" — e, ao ler isso, ele pediu *"mostrar
 * as 40"*. O Brik respondeu que não conseguia listar; a lista estava do lado,
 * na mesma resposta, porque o servidor tinha contado para escrever a frase.
 *
 * **Por que um diálogo e não 40 cartões.** Pedido dele, nas palavras dele:
 * *"acho que é melhor ter um link que abra uma modal do que aparecer a
 * listagem de 40"*. Quarenta cartões empurrariam a conversa para fora da tela
 * — e isto aqui não é um achado, é um ponteiro para ir ouvir o áudio.
 *
 * Não há botão de agir: o Brik não concluiu nada sobre elas. O que a linha
 * oferece é o caminho — abrir a oportunidade e ouvir.
 */
const AssistenteSemTexto: FC<{
    conversas: ConversaSemTexto[];
    onFechar: () => void;
    /** No celular não há nova aba que ajude: abre na mesma. */
    celular?: boolean;
}> = ({ conversas, onFechar, celular = false }) => {
    const fecharRef = useRef<HTMLButtonElement>(null);

    /* Esc fecha, com captura — o painel do Brik também escuta Esc, e sem isto
       ele fecharia por baixo e o diálogo ficaria sozinho na tela. O mesmo
       cuidado do `Dialog`. */
    useEffect(() => {
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            onFechar();
        };
        document.addEventListener('keydown', aoTeclar, true);
        return () => document.removeEventListener('keydown', aoTeclar, true);
    }, [onFechar]);

    useEffect(() => { fecharRef.current?.focus(); }, []);

    return (
        /* O clique no fundo fecha SÓ o diálogo: sem o stopPropagation ele
           chega ao `document`, e o painel do Brik — que fecha ao clique fora —
           sumiria junto, levando a conversa embora. */
        <div className="agt-st-fundo" onClick={e => { e.stopPropagation(); onFechar(); }}>
            <div
                className="agt-st"
                role="dialog"
                aria-modal="true"
                aria-label="Conversas que não deram para julgar"
                onClick={e => e.stopPropagation()}
            >
                <div className="agt-st-k">
                    <div>
                        <h2 className="agt-st-t">
                            {conversas.length === 1
                                ? '1 conversa que não deu para julgar'
                                : `${conversas.length} conversas que não deram para julgar`}
                        </h2>
                        <p className="agt-st-sub">
                            São de áudio, foto ou vídeo — o Brik lê texto. Abra para ouvir e, se a venda
                            estiver fechada ali, marque na própria oportunidade.
                        </p>
                    </div>

                    <button type="button" ref={fecharRef} className="agt-st-x" onClick={onFechar} aria-label="Fechar">
                        <LuX size={16} />
                    </button>
                </div>

                <ul className="agt-st-lista">
                    {conversas.map(c => (
                        <li key={c.oportunidadeId} className="agt-st-li">
                            <div className="agt-st-quem">
                                <span className="agt-st-nome">{c.contato}</span>
                                <span className="agt-st-num">nº {c.oportunidadeId}</span>
                            </div>

                            <div className="agt-st-meta">
                                <span>{c.etapa}</span>
                                <span className="agt-st-sep">·</span>
                                <span className="agt-st-midia">
                                    {c.audios > 0 && <><LuMic size={12} /> {c.audios}</>}
                                    {c.imagens > 0 && <><LuImage size={12} /> {c.imagens}</>}
                                    {c.videos > 0 && <><LuVideo size={12} /> {c.videos}</>}
                                    {c.audios + c.imagens + c.videos === 0
                                        && <>{c.mensagens} {c.mensagens === 1 ? 'mensagem' : 'mensagens'}</>}
                                </span>
                                <span className="agt-st-sep">·</span>
                                <span>{data(c.ultimaMensagemEm)}</span>
                            </div>

                            <a
                                className="agt-st-b"
                                href={c.rota}
                                target={celular ? undefined : '_blank'}
                                rel={celular ? undefined : 'noreferrer'}
                            >
                                <LuExternalLink size={13} /> Abrir
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default AssistenteSemTexto;
